/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * LDView — single legislative district deep dive.
 *
 * Fully wired to the backend as of 2026-04-10. On mount we fetch
 *   GET /voters/by-legislative-district/{ld}
 * which returns core metrics + tier / party distributions + a
 * per-precinct breakdown + real past-election turnout joined from
 * voter_elections. Subregion navigation chrome comes from the tenant's turf
 * catalog (GET /geo/regions, via MapRegionsContext).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { ROUTES, LD_COLORS } from '@/data/dashboardData';
import { useMapRegions } from '@/context/MapRegionsContext';
import { regionsInLd } from '@/api/geoRegions';
import { LD_NAMES } from '@/lib/computeStats';
import { Breadcrumb } from '@/components/shared';
import client from '@/api/client';
import { fetchPrecinctSparklines } from '@/api/analytics';
import type { PrecinctSparklinesResponse } from '@/types/analytics';
import { Sparkline } from '@/components/Sparkline';
import { InfoTip } from '@/components/InfoTip';
import { ColumnLabel } from '@/components/MetricLabel';
import { TierLegend } from '@/components/TierLegend';
import { VisualKey } from '@/components/VisualKey';
import { PrecinctStandouts } from '@/components/PrecinctStandouts';
import { TierPill } from '@/components/TierPill';
import { DeltaChip } from '@/components/DeltaChip';
import { BarMeter } from '@/components/BarMeter';
import { RankBadge } from '@/components/RankBadge';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  useCountyBaselines,
  computePeerBaselines,
  rankWithin,
} from '@/lib/baselines';
import {
  OnboardingModal,
  OnboardingHelpButton,
} from '@/components/OnboardingModal';
import { ViewHeader } from '@/components/ViewHeader';

// -------------------------------------------------------------------------
// Types mirroring backend/app/schemas/voter.py → LegislativeDistrictDetailResponse
// -------------------------------------------------------------------------

type ElectionHistoryPoint = {
  election_date: string;
  election_type: string;
  voter_count: number;
};

type PrecinctSummary = {
  precinct_num: string;
  precinct_name: string;
  total_voters: number;
  active_pct: number;
  avg_score: number;
  super_voter_pct: number;
  primary_voter_pct: number;
};

type LegislativeDistrictDetail = {
  district_key: string;
  district_raw: string;
  total_voters: number;
  active_voters: number;
  avg_score: number;
  super_voter_pct: number;
  primary_voter_pct: number;
  by_tier: Record<string, number>;
  by_party: Record<string, number>;
  precincts: PrecinctSummary[];
  election_history: ElectionHistoryPoint[];
};

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

const TIER_DISPLAY_ORDER = ['SUPER_VOTER', 'HOT', 'WARM', 'COOL'];
const TIER_LABELS: Record<string, string> = {
  SUPER_VOTER: 'Super Voter',
  HOT: 'Hot',
  WARM: 'Warm',
  COOL: 'Cool',
};

const PARTY_LABELS: Record<string, string> = {
  R: 'Republican',
  D: 'Democratic',
  I: 'Independent',
  L: 'Libertarian',
  G: 'Green',
  C: 'Constitution',
  UNKNOWN: 'No Party',
};

function toChartData(
  dict: Record<string, number>,
  order?: string[],
  labels?: Record<string, string>,
) {
  const keys = order ?? Object.keys(dict);
  return keys
    .filter((k) => (dict[k] ?? 0) > 0 || order !== undefined)
    .map((k) => ({
      name: labels?.[k] ?? k,
      value: dict[k] ?? 0,
    }));
}

// -------------------------------------------------------------------------
// Stat tile
// -------------------------------------------------------------------------

function Stat({
  label,
  value,
  suffix = '',
  termId,
  children,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  termId?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-bg-raised border border-border-base px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted flex items-center">
        <span>{label}</span>
        {termId && <InfoTip termId={termId} compact />}
      </div>
      <div className="mt-0.5 text-xl font-bold text-fg-primary tabular-nums">
        {value}
        {suffix}
      </div>
      {children && <div className="mt-1 space-y-1">{children}</div>}
    </div>
  );
}

// -------------------------------------------------------------------------
// Main view
// -------------------------------------------------------------------------

export const LDView: React.FC<any> = ({ ld, onDrill, onBack }) => {
  const [data, setData] = useState<LegislativeDistrictDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sparklines, setSparklines] = useState<PrecinctSparklinesResponse | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);
  const { baselines: countyBl } = useCountyBaselines();
  const reduceMotion = useReducedMotion();

  // The `ld` prop comes in as a number ("12" / 12 / "14") — normalize to
  // the short string form the backend expects.
  const ldKey = String(ld);
  const { catalog: regions } = useMapRegions();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSparklines(null);
    client
      .get<LegislativeDistrictDetail>(
        `/voters/by-legislative-district/${encodeURIComponent(ldKey)}`,
      )
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setLoading(false);
        // Kick off the bulk sparkline fetch for this LD's precincts in the
        // background. Failures are non-fatal — the table just renders without
        // the trend column. This is a single round-trip instead of N calls.
        if (res.data.precincts.length > 0) {
          // Bulk fetch — one request returns sparklines for every
          // precinct in the tenant; we filter client-side to the LD
          // we're rendering. Avoids the per-row N+1 problem.
          fetchPrecinctSparklines({
            lookback: 6,
            electionType: 'General',
            topNPrecincts: 200,
          })
            .then((sp) => {
              if (!cancelled) setSparklines(sp);
            })
            .catch(() => {
              /* sparklines are an enhancement, not load-blocking */
            });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 404)
          setError(`Legislative district ${ldKey} not found in the database.`);
        else if (code === 401)
          setError('Not authenticated — log in to see live district data.');
        else setError(err?.message ?? 'Unknown error loading district data.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ldKey]);

  // Build a precinct_num → values[] map for fast lookup in the table
  // render. The backend returns SparklinePoint[] aligned to the shared
  // X axis; we project down to a flat voter_count array for the
  // dependency-free Sparkline component.
  const sparklineMap = useMemo(() => {
    const m = new Map<string, { values: number[]; labels: string[] }>();
    if (sparklines) {
      const labels = sparklines.elections.map((e) => e.label);
      for (const p of sparklines.precincts) {
        // Project sparse series back onto the dense X axis.
        const byDate = new Map(
          p.series.map((pt) => [pt.election_date, pt.voter_count]),
        );
        const values = sparklines.elections.map((e) => byDate.get(e.election_date) ?? 0);
        m.set(p.precinct_num, { values, labels });
      }
    }
    return m;
  }, [sparklines]);

  // Subregions come from the tenant's turf catalog, not the voter roll —
  // "subregion" is a turf-assignment construct with no DB equivalent on a
  // voter row. Empty while the catalog loads, and empty for a tenant with no
  // turfs; the card block below already hides itself on an empty list.
  const subregions = useMemo(
    () => regionsInLd(regions, ldKey).map((r) => ({ code: r.key, ...r })),
    [regions, ldKey],
  );

  // ---------- loading state ----------
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-border-base bg-bg-raised/60 p-12 text-center">
          <div className="inline-block h-3 w-3 rounded-full bg-emerald-500 animate-pulse mr-2" />
          <span className="text-fg-secondary">
            Loading {LD_NAMES[ld] || `LD ${ldKey}`} from Postgres...
          </span>
        </div>
      </div>
    );
  }

  // ---------- error state ----------
  if (error || !data) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-status-danger bg-status-danger-bg/40 p-6 text-status-danger">
          <div className="font-semibold mb-1">
            Couldn't load {LD_NAMES[ld] || `LD ${ldKey}`}
          </div>
          <div className="text-sm text-status-danger/80">{error}</div>
        </div>
      </div>
    );
  }

  // ---------- success state: derive chart data ----------
  const tierData = toChartData(data.by_tier, TIER_DISPLAY_ORDER, TIER_LABELS);
  const partyData = toChartData(data.by_party, undefined, PARTY_LABELS).sort(
    (a, b) => b.value - a.value,
  );
  const activePct = data.total_voters
    ? (data.active_voters * 100) / data.total_voters
    : 0;

  // Precinct peer baselines (for indicators in the precinct table)
  const precinctPeers = computePeerBaselines(data.precincts as any);

  // Turnout history chart: newest-first → oldest-first for the X axis.
  const turnoutData = [...data.election_history]
    .sort((a, b) => a.election_date.localeCompare(b.election_date))
    .map((e) => ({
      name: e.election_date.slice(0, 10),
      fullLabel: `${e.election_date} ${e.election_type}`,
      voters: e.voter_count,
      type: e.election_type,
    }));

  const ldColor = LD_COLORS[ld] || '#dc2626';

  return (
    <div className="space-y-6">
      <OnboardingModal
        viewKey="ldview"
        title="Legislative District Detail"
        intro="A single Missouri House district, broken down by tier, party, and precinct. The Past Election Turnout chart shows how many voters from this district have shown up over time. Click any precinct row in the table at the bottom to drill in further."
        termIds={[
          'legislative_district',
          'tier_overview',
          'avg_score',
          'super_voter_pct',
          'primary_voter',
          'sparkline',
        ]}
        workflows={[
          'Compare this district\'s avg score to the county average to gauge friendliness.',
          'Spot precincts with collapsing trend lines in the table — those need re-engagement.',
          'Click a subregion to print the matching walk sheet for coordinators.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => setForceOnboard(false)}
      />
      <Breadcrumb
        items={[
          { label: 'County Map', action: 'map' },
          { label: LD_NAMES[ld] || `LD ${ldKey}`, action: null },
        ]}
        onNavigate={onDrill}
      />

      {/* Header strip */}
      <ViewHeader
        title={LD_NAMES[ld] || `Legislative District ${ldKey}`}
        tagline={`Missouri House District ${ldKey} · ${data.district_raw} · ${data.precincts.length} precincts`}
        inline={<InfoTip termId="legislative_district" />}
        actions={<OnboardingHelpButton onClick={() => setForceOnboard(true)} />}
      />

      {/* Tier legend strip + indicator key */}
      <TierLegend />
      <VisualKey />

      {/* Standout child precincts — at-a-glance, drills into the precinct */}
      <PrecinctStandouts precincts={data.precincts} onDrill={onDrill} />

      {/* Headline stats row (all live) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat
          label="Total Voters"
          value={data.total_voters.toLocaleString()}
          termId="data_freshness"
        />
        <Stat
          label="Active %"
          value={activePct.toFixed(1)}
          suffix="%"
          termId="active_voter"
        >
          <BarMeter value={activePct} tierColor={false} />
          {countyBl && (
            <DeltaChip
              value={activePct}
              baseline={countyBl.activePct}
              label="vs county"
              format="pct"
              compact
            />
          )}
        </Stat>
        <Stat
          label="Avg Score"
          value={data.avg_score.toFixed(1)}
          termId="avg_score"
        >
          <div className="flex items-center gap-1 flex-wrap">
            <TierPill score={data.avg_score} size="sm" />
            {countyBl && (
              <DeltaChip
                value={data.avg_score}
                baseline={countyBl.avgScore}
                label="vs county"
                format="num"
                compact
              />
            )}
          </div>
          <BarMeter
            value={data.avg_score}
            baseline={countyBl?.avgScore ?? null}
          />
        </Stat>
        <Stat
          label="Super Voter %"
          value={data.super_voter_pct.toFixed(2)}
          suffix="%"
          termId="super_voter_pct"
        >
          {countyBl && (
            <DeltaChip
              value={data.super_voter_pct}
              baseline={countyBl.superVoterPct}
              label="vs county"
              format="pct"
              compact
            />
          )}
          <BarMeter value={data.super_voter_pct} tierColor={false} />
        </Stat>
        <Stat
          label="Primary Voter %"
          value={data.primary_voter_pct.toFixed(1)}
          suffix="%"
          termId="primary_voter"
        >
          {countyBl && (
            <DeltaChip
              value={data.primary_voter_pct}
              baseline={countyBl.primaryVoterPct}
              label="vs county"
              format="pct"
              compact
            />
          )}
          <BarMeter value={data.primary_voter_pct} tierColor={false} />
        </Stat>
        <Stat
          label="Past Elections"
          value={data.election_history.length.toLocaleString()}
          termId="roster_turnout"
        />
      </div>

      {/* Turnout History chart — unlocked by voter_elections */}
      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
            Past Election Turnout
            <InfoTip termId="roster_turnout" />
          </h2>
          <span className="text-xs text-fg-muted">
            Voters from this district who cast a ballot · newest {turnoutData.length}{' '}
            elections
          </span>
        </div>
        {turnoutData.length > 0 ? (() => {
          const firstT = turnoutData[0];
          const lastT = turnoutData[turnoutData.length - 1];
          const delta = lastT.voters - firstT.voters;
          const dir = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
          const pct = firstT.voters
            ? ((delta / firstT.voters) * 100).toFixed(1)
            : '0';
          const ldLabel = LD_NAMES[ld] || `LD ${ldKey}`;
          const shortLabel = `Line chart: past-election turnout for ${ldLabel} across ${turnoutData.length} elections, ${dir} ${Math.abs(Number(pct))}% from ${firstT.voters.toLocaleString()} to ${lastT.voters.toLocaleString()}.`;
          const longNarrative = `Past election turnout for ${ldLabel}: ${turnoutData
            .map((t) => `${t.fullLabel} ${t.voters.toLocaleString()} voters`)
            .join('; ')}. Overall ${dir} ${Math.abs(Number(pct))}% from first to last recorded election.`;
          return (
            <figure
              aria-label={shortLabel}
              className="m-0"
            >
              <figcaption className="sr-only">{longNarrative}</figcaption>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={turnoutData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fill: 'var(--chart-axis)' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgb(var(--bg-elevated))',
                      border: '1px solid rgb(var(--border-base))',
                      color: 'rgb(var(--fg-primary))',
                    }}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.fullLabel ?? ''
                    }
                    formatter={(value: number) => [value.toLocaleString(), 'Voters']}
                  />
                  <Line
                    type="monotone"
                    dataKey="voters"
                    stroke={ldColor}
                    strokeWidth={2}
                    dot={{ r: 3, fill: ldColor }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={!reduceMotion}
                  />
                </LineChart>
              </ResponsiveContainer>
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                  Show data as table
                </summary>
                <table className="mt-2 w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-1 px-2 text-fg-muted">Election</th>
                      <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                    </tr>
                  </thead>
                  <tbody>
                    {turnoutData.map((t) => (
                      <tr key={t.fullLabel} className="border-t border-border-base">
                        <td className="py-1 px-2 text-fg-secondary">{t.fullLabel}</td>
                        <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                          {t.voters.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            </figure>
          );
        })() : (
          <div className="text-sm text-fg-muted py-10 text-center">
            No past-election records in this district yet.
          </div>
        )}
      </div>

      {/* Tier + Party row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Engagement Tiers
            <InfoTip termId="tier_overview" />
          </h2>
          {(() => {
            const ldLabel = LD_NAMES[ld] || `LD ${ldKey}`;
            const top = [...tierData].sort((a, b) => b.value - a.value)[0];
            const total = tierData.reduce((s, d) => s + d.value, 0);
            const topPct = total > 0 ? ((top.value / total) * 100).toFixed(1) : '0';
            const shortLabel = `Bar chart: engagement tier distribution across ${tierData.length} tiers for ${ldLabel}. ${top.name} dominates with ${top.value.toLocaleString()} voters (${topPct}% of the district).`;
            const longNarrative = `Engagement tiers in ${ldLabel}: ${tierData
              .map(
                (d) =>
                  `${d.name} ${d.value.toLocaleString()} voters (${
                    total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'
                  }%)`,
              )
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={tierData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="name" tick={{ fill: 'var(--chart-axis)' }} />
                    <YAxis tick={{ fill: 'var(--chart-axis)' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgb(var(--bg-elevated))',
                        border: '1px solid rgb(var(--border-base))',
                        color: 'rgb(var(--fg-primary))',
                      }}
                      formatter={(value: number) => value.toLocaleString()}
                    />
                    <Bar dataKey="value" fill="var(--accent)" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Tier</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierData.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
        </div>

        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            Party Registration
          </h2>
          {(() => {
            const ldLabel = LD_NAMES[ld] || `LD ${ldKey}`;
            const top = partyData[0];
            const total = partyData.reduce((s, d) => s + d.value, 0);
            const topPct = total > 0 && top ? ((top.value / total) * 100).toFixed(1) : '0';
            const shortLabel = top
              ? `Bar chart: party registration across ${partyData.length} parties for ${ldLabel}. ${top.name} leads with ${top.value.toLocaleString()} voters (${topPct}%).`
              : `Bar chart: party registration for ${ldLabel}. No data.`;
            const longNarrative = `Party registration in ${ldLabel}: ${partyData
              .map(
                (d) =>
                  `${d.name} ${d.value.toLocaleString()} voters (${
                    total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'
                  }%)`,
              )
              .join('; ')}.`;
            return (
              <figure aria-label={shortLabel} className="m-0">
                <figcaption className="sr-only">{longNarrative}</figcaption>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={partyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                    <XAxis dataKey="name" tick={{ fill: 'var(--chart-axis)' }} />
                    <YAxis tick={{ fill: 'var(--chart-axis)' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgb(var(--bg-elevated))',
                        border: '1px solid rgb(var(--border-base))',
                        color: 'rgb(var(--fg-primary))',
                      }}
                      formatter={(value: number) => value.toLocaleString()}
                    />
                    <Bar dataKey="value" fill="var(--chart-series-2)" isAnimationActive={!reduceMotion} />
                  </BarChart>
                </ResponsiveContainer>
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-fg-secondary hover:text-fg-primary">
                    Show data as table
                  </summary>
                  <table className="mt-2 w-full">
                    <thead>
                      <tr>
                        <th className="text-left py-1 px-2 text-fg-muted">Party</th>
                        <th className="text-right py-1 px-2 text-fg-muted">Voters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partyData.map((d) => (
                        <tr key={d.name} className="border-t border-border-base">
                          <td className="py-1 px-2 text-fg-secondary">{d.name}</td>
                          <td className="py-1 px-2 text-right text-fg-secondary tabular-nums">
                            {d.value.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </figure>
            );
          })()}
        </div>
      </div>

      {/* Subregions — turf-catalog metadata, shown as nav cards */}
      {subregions.length > 0 && (
        <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-4">
            Subregions in {LD_NAMES[ld] || `LD ${ldKey}`}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {subregions.map((sr) => (
              <div
                key={sr.code}
                onClick={() => onDrill('subregion', sr.code)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onDrill('subregion', sr.code);
                  }
                }}
                tabIndex={0}
                className="bg-bg-base hover:bg-bg-elevated rounded-lg p-4 border-l-4 transition cursor-pointer text-left"
                style={{ borderColor: ldColor }}
              >
                <div className="font-bold text-fg-primary">
                  {sr.code} — {sr.name}
                </div>
                <div className="mt-1 text-xs text-fg-muted">
                  {sr.precincts?.length || 0} precincts · Routes:{' '}
                  {ROUTES.subregions?.[sr.code]?.doors || 0}
                </div>
                {sr.mapFile && (
                  <div className="mt-2">
                    <a
                      href={`/maps/${sr.mapFile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-fg hover:text-accent text-xs font-medium inline-flex items-center justify-center min-h-11 sm:min-h-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Subregion Map
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-precinct table — fully live */}
      <div className="bg-bg-raised rounded-xl p-6 border border-border-base min-w-0">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
            Precincts in {LD_NAMES[ld] || `LD ${ldKey}`}
            <InfoTip termId="precinct_comparison" />
          </h2>
          <span className="text-xs text-fg-muted">
            {data.precincts.length} precincts · ordered by voter count · click any row to drill in
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-base">
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="precinct">Precinct</ColumnLabel>
                </th>
                <th className="text-right py-2 px-3 text-fg-muted font-semibold">
                  Total
                </th>
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="active_voter">Active %</ColumnLabel>
                </th>
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="avg_score">Avg Score</ColumnLabel>
                </th>
                <th className="text-center py-2 px-3 text-fg-muted font-semibold">
                  LD Rank
                </th>
                <th className="text-right py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="super_voter_pct">Super Voter %</ColumnLabel>
                </th>
                <th className="text-right py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="primary_voter">Primary %</ColumnLabel>
                </th>
                <th className="text-center py-2 px-3 text-fg-muted font-semibold">
                  <ColumnLabel termId="sparkline">Trend</ColumnLabel>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.precincts.map((p) => {
                const scoreRank = rankWithin(
                  data.precincts as any,
                  (r: any) => r.avg_score,
                  p.avg_score,
                  'higher-better',
                );
                return (
                <tr
                  key={p.precinct_num}
                  onClick={() => onDrill('precinct', p.precinct_num)}
                  className="border-b border-border-base hover:bg-bg-elevated cursor-pointer transition"
                >
                  <td className="py-2 px-3 text-fg-primary font-medium">
                    {p.precinct_num} {p.precinct_name}
                  </td>
                  <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                    {p.total_voters.toLocaleString()}
                  </td>
                  <td className="py-2 px-3 text-left text-fg-secondary min-w-[140px]">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="tabular-nums">
                        {p.active_pct.toFixed(1)}%
                      </span>
                      {countyBl && (
                        <DeltaChip
                          value={p.active_pct}
                          baselines={[
                            { label: 'vs LD', value: activePct },
                            {
                              label: 'peer med',
                              value: precinctPeers.activePct,
                            },
                            { label: 'vs county', value: countyBl.activePct },
                          ]}
                          format="pct"
                          compact
                        />
                      )}
                    </div>
                    <BarMeter
                      value={p.active_pct}
                      baseline={activePct}
                      tierColor={false}
                      height={3}
                    />
                  </td>
                  <td className="py-2 px-3 text-left text-fg-primary min-w-[160px]">
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                      <span className="font-semibold tabular-nums">
                        {p.avg_score.toFixed(1)}
                      </span>
                      <TierPill score={p.avg_score} size="sm" />
                      <DeltaChip
                        value={p.avg_score}
                        baselines={[
                          { label: 'vs LD', value: data.avg_score },
                          {
                            label: 'peer med',
                            value: precinctPeers.avgScore,
                          },
                          ...(countyBl
                            ? [
                                {
                                  label: 'vs county',
                                  value: countyBl.avgScore,
                                },
                              ]
                            : []),
                        ]}
                        format="num"
                        compact
                      />
                    </div>
                    <BarMeter
                      value={p.avg_score}
                      baseline={data.avg_score}
                      height={3}
                    />
                  </td>
                  <td className="py-2 px-3 text-center">
                    <RankBadge
                      rank={scoreRank.rank}
                      total={scoreRank.total}
                    />
                  </td>
                  <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                    {p.super_voter_pct.toFixed(2)}%
                  </td>
                  <td className="py-2 px-3 text-right text-fg-secondary tabular-nums">
                    {p.primary_voter_pct.toFixed(1)}%
                  </td>
                  <td className="py-2 px-3 text-center">
                    {(() => {
                      const entry = sparklineMap.get(p.precinct_num);
                      if (!entry || entry.values.length === 0) {
                        return (
                          <span className="text-[10px] text-fg-muted">—</span>
                        );
                      }
                      const first = entry.values[0];
                      const last = entry.values[entry.values.length - 1];
                      const delta = last - first;
                      const dir =
                        delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
                      const pct = first
                        ? ((delta / Math.abs(first)) * 100).toFixed(0)
                        : '0';
                      return (
                        <div className="inline-block align-middle">
                          <Sparkline
                            values={entry.values}
                            labels={entry.labels}
                            format={(v) => `${v.toLocaleString()} voters`}
                            ariaLabel={`Trend for precinct ${p.precinct_num} ${p.precinct_name}: turnout ${dir} ${Math.abs(Number(pct))}% over ${entry.values.length} elections, from ${first.toLocaleString()} to ${last.toLocaleString()} voters.`}
                          />
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};
